import { useEffect, useState } from "react";
import { t } from "i18next";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";

import {
  useGetTopicsQuery,
  useLazyPaginateFilterDiscussionsQuery,
} from "@/redux/service";
import BasePagination from "@/components_v2/Pagination/BasePagination";
import { PaginatedResponse } from "@/redux/types";
import {
  BaseDiscussionResponse,
  DiscussionQueryParams,
} from "@/redux/discussions/types";
import {
  DiscussionFiltersState,
  DiscussionListProps,
  DiscussionsDataState,
} from "@/domains/community/types";
import DiscussionFilters from "@/domains/community/components/DiscussionFilters";
import BaseDivider from "@/components_v2/Divider/BaseDivider";
import BaseTag from "@/components_v2/Tags/BaseTag";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import DiscussionIcon from "@/assets/icons/discussions-icon.svg?react";
import EyeIcon from "@/assets/icons/eye.svg?react";
import SearchXIcon from "@/assets/icons/search-x.svg?react";
import { TopicResponse } from "@/redux/comments/types";
import { PAGINATION } from "@/shared/utils/constants";
import TextElement from "@/components_v2/ContentElements/TextElement";
import { TextType } from "@/components_v2/ContentElements/types";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";

const DiscussionList = ({ defaultFilters }: DiscussionListProps) => {
  const [getDiscussions, { isLoading }] =
    useLazyPaginateFilterDiscussionsQuery();
  const [filters, setFilters] = useState({});
  const [discussionsData, setDiscussionsData] = useState<DiscussionsDataState>({
    page: PAGINATION.PAGE.DEFAULT,
    total: 0,
    data: [],
  });
  const { data: discussionTopics } = useGetTopicsQuery({
    pagination: "off",
  }) as { data: TopicResponse[] };
  const [topics, setTopics] = useState<Record<number, string>>({});

  useEffect(() => {
    if (discussionTopics) {
      const topicNames = discussionTopics.reduce<Record<number, string>>(
        (acc, topic) => {
          acc[topic.id] = topic.title;
          return acc;
        },
        {},
      );
      setTopics({ ...topics, ...topicNames });
    }
  }, [discussionTopics]);

  const transformFiltersForAPI = (
    filters: DiscussionFiltersState,
  ): DiscussionQueryParams => {
    const { createdRange, ...rest } = filters;

    const transformed: DiscussionQueryParams = {
      ...rest,
    };

    if (
      createdRange &&
      Array.isArray(createdRange) &&
      createdRange.length === 2
    ) {
      const [from, to] = createdRange;
      if (from) transformed.startDate = from.toFormat("yyyy-MM-dd");
      if (to) transformed.endDate = to.toFormat("yyyy-MM-dd");
    }

    return transformed;
  };

  const fetchDiscussions = async (
    page = 1,
    currentFilters: DiscussionFiltersState = filters,
  ) => {
    const processedFilters = transformFiltersForAPI(currentFilters);
    const response = (await getDiscussions({
      page: page,
      pageSize: PAGINATION.PAGE_SIZE.DEFAULT,
      ...defaultFilters,
      ...processedFilters,
    }).unwrap()) as PaginatedResponse<BaseDiscussionResponse>;

    setDiscussionsData({
      page: page,
      total: response.count,
      data: response.results,
    });
  };

  useEffect(() => {
    fetchDiscussions(1);
  }, []);

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      fetchDiscussions(1, filters);
    }
  }, [filters]);

  const handlePagination = (newPage: number) => {
    if (newPage !== discussionsData.page && newPage > 0) {
      fetchDiscussions(newPage);
    }
  };

  return (
    <div className="table-container-layout">
      <DiscussionFilters
        setFilters={setFilters}
        filterNames={["search", "topics", "createdRange"]}
        defaultFilters={defaultFilters}
      />

      {isLoading && <BaseSpinner />}

      <div className="flex flex-col gap-400">
        {discussionsData.data.map((discussion) => (
          <div
            key={discussion.id}
            className="discussion-item flex flex-col gap-250 items-start"
          >
            <Link to={`/discussions/${discussion.id}`}>
              <h3 className="header-s-medium">{discussion.title}</h3>
            </Link>

            <p className="body-m-regular">
              <TextElement
                value={discussion.content}
                type={TextType.RICH_TEXT}
                lineClampNumber={3}
              />
            </p>

            <BaseDivider />

            <div className="discussion-item-footer body-s-regular flex flex-wrap justify-between items-center self-stretch gap-200">
              <div className="flex flex-wrap gap-200 items-center">
                {t("community.labels.postedOnDateIn", {
                  date: DateTime.fromISO(discussion.createdAt).toFormat(
                    "dd.MM.yyyy",
                  ),
                })}

                {discussion.topics.map((topic) => (
                  <BaseTag
                    size="small"
                    key={topic}
                    label={
                      <Link to={`/community/topics/${topics[topic]}`}>
                        {topics[topic] || ""}
                      </Link>
                    }
                  />
                ))}
              </div>

              <div className="flex flex-wrap gap-250 items-center">
                <div className="flex items-center gap-100">
                  <BaseSvgRenderer component={DiscussionIcon} />
                  {t("community.labels.comments", {
                    count: discussion.commentsCount,
                  })}
                </div>
                <div className="flex items-center gap-100">
                  <BaseSvgRenderer component={EyeIcon} />
                  {t("community.labels.features", { count: discussion.views })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {discussionsData.data.length > 0 && (
        <BasePagination
          current={discussionsData.page}
          total={discussionsData.total}
          pageSize={PAGINATION.PAGE_SIZE.DEFAULT}
          onChange={handlePagination}
        />
      )}

      {discussionsData.data.length === 0 && !isLoading && (
        <div className="empty-state-container full-width flex flex-col items-center justify-center">
          <BaseSvgRenderer component={SearchXIcon} />
          <h6 className="header-m-medium">
            {t("community.labels.noDiscussionsFound")}
          </h6>
        </div>
      )}
    </div>
  );
};

export default DiscussionList;
